import type {DiffAnchor} from '@github-ui/diffs/types'
import {useAnalytics} from '@github-ui/use-analytics'
import {type FocusEvent, useMemo, useRef} from 'react'

import type {StartThreadCommentProps} from './StartThreadComment'
import {StartThreadComment} from './StartThreadComment'
import {useGetSelectedLineRange, useTreeEntries} from '../../stores/selectors'
import type {DiffLine} from '../../types'
import {useSuggestedChanges} from '../../hooks/use-suggested-changes'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCreateReviewCommentMutation} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {ReviewCommentFactory} from '../../helpers/review-comment-factory'
import {reviewCommentIsMultiline} from '../../helpers/review-comment-is-multiline'
import {isContextDiffLine} from '../../helpers/line-helpers'
import {useComparisonInfo, useSelectedRange} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useCommentDialogTitle} from '../../hooks/use-comment-dialog-title'

export interface StartConversationProps
  extends Pick<
    StartThreadCommentProps,
    | 'batchingEnabled'
    | 'fileLevelComment'
    | 'filePath'
    | 'focusOnMount'
    | 'isLeftSide'
    | 'lineNumber'
    | 'viewerData'
    | 'threadsConnectionId'
  > {
  diffAnchor: DiffAnchor
  diffLine: DiffLine
  isInDialogMode: boolean
  isOpen: boolean
  onAddComment?: StartThreadCommentProps['onAddComment']
  startLineNumber?: StartThreadCommentProps['startLineNumber']
  onCloseCommentDialog: (shouldFocusCell?: boolean) => void
  shouldStartNewConversationWithSuggestedChange?: boolean
}

/**
 * Renders a modal AnchoredOverlay for starting a new conversation thread or an in line version
 */
export function StartConversation({
  diffAnchor,
  isInDialogMode,
  isOpen,
  onCloseCommentDialog,
  onAddComment,
  startLineNumber,
  diffLine,
  shouldStartNewConversationWithSuggestedChange,
  ...rest
}: StartConversationProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const newCommentContainerRef = useRef<HTMLDivElement>(null)
  const getSelectedLineRange = useGetSelectedLineRange()
  //todo: make this something passed in from the parent once luke's changes are in
  const isRowSelected = false
  const suggestedChangesConfig = useSuggestedChanges(diffLine, shouldStartNewConversationWithSuggestedChange)
  const {newTreeEntry, oldTreeEntry} = useTreeEntries()
  const {effectiveRange: effectiveDiffRange} = useComparisonInfo()
  const {baseOid: startOid, headOid: endOid} = useSelectedRange()
  const commentOutsideOfTheDiffEnabled = useFeatureFlag('cotd_ui')
  const isLeftSide = diffLine?.type === 'DELETION'
  const addCommentDialogTitle = useCommentDialogTitle(diffLine, !!isLeftSide, isRowSelected)
  const startConversationStartLineNumber = useMemo(() => {
    if (!isRowSelected) return

    const selectedLineRange = getSelectedLineRange()
    return selectedLineRange?.startLineNumber
  }, [getSelectedLineRange, isRowSelected])

  const {mutate: createReviewCommentMutation} = useCreateReviewCommentMutation()
  const handleAddComment = ({
    onCompleted,
    onError,
    submitBatch,
    text,
  }: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => {
    if (!diffLine) return

    const selectedLineRange = getSelectedLineRange()

    if (commentOutsideOfTheDiffEnabled) {
      const requestOpts = {
        filePath: rest.filePath,
        newTreeEntry,
        oldTreeEntry,
        effectiveDiffRange,
        submitBatch,
        text,
      }
      let requestData
      const reviewCommentFactory = new ReviewCommentFactory(requestOpts)

      if (selectedLineRange && reviewCommentIsMultiline(isRowSelected, selectedLineRange))
        requestData = reviewCommentFactory.multilineRequestData(selectedLineRange)
      else {
        requestData = reviewCommentFactory.lineRequestData(diffLine, isLeftSide)
      }

      createReviewCommentMutation(requestData, {
        onError,
        onSuccess: data => {
          onCompleted?.(data.thread.id, data.comment?.databaseId)
          //TODO: dialog mode and also making sure adding a comment updates the thread rendering
          //enterDialogMode()
          //setManuallyUpdateComments(data.thread.id)
        },
      })
    } else {
      const isMultiLineComment: boolean =
        selectedLineRange && selectedLineRange.startLineNumber !== selectedLineRange.endLineNumber ? true : false
      let endLine: number = diffLine.blobLineNumber
      let side: 'left' | 'right' = isLeftSide && !isContextDiffLine(diffLine) ? 'left' : 'right'
      let startLine: number | undefined
      let startSide: 'left' | 'right' | undefined

      if (selectedLineRange && isMultiLineComment && isRowSelected) {
        side = selectedLineRange.endOrientation === 'left' ? 'left' : 'right'
        startSide = selectedLineRange.startOrientation === 'left' ? 'left' : 'right'
        endLine = selectedLineRange.endLineNumber
        startLine = selectedLineRange.startLineNumber
      }

      createReviewCommentMutation(
        {
          text,
          line: endLine,
          path: rest.filePath,
          side,
          startSide,
          submitBatch,
          startLine,
          comparisonEndOid: endOid,
          comparisonStartOid: startOid,
        },
        {
          onError,
          onSuccess: data => {
            onCompleted?.(data.thread.id, data.comment?.databaseId)
            //TODO: dialog mode and also making sure adding a comment updates the thread rendering
            //enterDialogMode()
            //setManuallyUpdateComments(data.thread.id)
          },
        },
      )
    }
  }

  const config = useMemo(() => {
    if (isOpen && suggestedChangesConfig?.configureSuggestedChangesFromLineRange) {
      const selectedLineRange = getSelectedLineRange()
      return suggestedChangesConfig.configureSuggestedChangesFromLineRange(
        selectedLineRange,
        suggestedChangesConfig?.shouldStartNewConversationWithSuggestedChange,
      )
    }
    return undefined
  }, [isOpen, suggestedChangesConfig, getSelectedLineRange])

  if (!isOpen) return null

  const handleNewCommentContainerFocus = (e: FocusEvent) => {
    // Only handle focus logic if a user is on the new comment container's first <div> element.
    if (e.target !== newCommentContainerRef.current) return

    // Focus on the textarea element inside of the new comment container
    newCommentContainerRef.current.querySelector('textarea')?.focus()
  }

  return (
    <div
      className="rounded-2 bgColor-default"
      data-marker-navigation-new-thread
      data-marker-id="new-comment" // Add data-marker-id attribute for keyboard navigation
      ref={newCommentContainerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
      onFocus={handleNewCommentContainerFocus}
    >
      <StartThreadComment
        onClose={(shouldFocusCell?: boolean) => {
          onCloseCommentDialog(shouldFocusCell)
          sendAnalyticsEvent('comments.cancel_thread_reply', 'CANCEL_REVIEW_THREAD_BUTTON')
        }}
        showOnCloseIcon={false}
        suggestedChangesConfig={config}
        onAddComment={onAddComment ? onAddComment : handleAddComment}
        addCommentDialogTitle={addCommentDialogTitle}
        startLineNumber={startLineNumber ?? startConversationStartLineNumber}
        {...rest}
      />
    </div>
  )
}

try{ StartConversation.displayName ||= 'StartConversation' } catch {}